<template>
    <div class="overflow-hidden bg-white shadow sm:rounded-lg">
        <div class="px-4 py-5 sm:px-6">
            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ title }}</h3>

            <dl class="mt-3 text-sm">
                <dt class="text-gray-500">{{ $t('Folder on the file server') }}</dt>
                <dd class="mt-1">
                    <code class="text-xs text-gray-700 break-all">{{ section.absolute_path }}</code>
                </dd>
            </dl>

            <p class="mt-3 text-sm text-gray-500">
                <span class="font-medium text-gray-900">
                    {{ $t(':count files lie here right now', {count: section.total_files}) }}
                </span>
                <template v-if="section.total_files > 0">
                    <span aria-hidden="true"> &middot; </span>
                    <span>{{ $t(':count would be imported', {count: section.would_import_count}) }}</span>
                </template>
                <template v-if="section.problem_count > 0">
                    <span aria-hidden="true"> &middot; </span>
                    <span class="font-medium text-danger-700">
                        {{ $t(':count need attention', {count: section.problem_count}) }}
                    </span>
                </template>
                <template v-if="section.duplicate_count > 0">
                    <span aria-hidden="true"> &middot; </span>
                    <span class="text-warning-800">
                        {{ $t(':count already exist', {count: section.duplicate_count}) }}
                    </span>
                </template>
            </p>
        </div>

        <div class="border-t border-gray-200 px-4 py-5 sm:px-6 space-y-4">
            <AlertBox v-if="!section.directory_exists" type="danger">
                {{ $t('This folder is missing on the file server. Create it, upload the files, then re-check.') }}
            </AlertBox>

            <AlertBox v-if="section.subdirectories.length > 0" type="warning">
                {{ $t('The import only reads files that lie loose in the folder. It never looks inside subfolders, so nothing in these will be imported:') }}
                <ul class="mt-2 space-y-1">
                    <li v-for="directory in section.subdirectories" :key="directory.path">
                        <code class="text-xs break-all">{{ directory.name }}</code>
                        <span class="text-xs"> — {{ $t(':count files', {count: directory.file_count}) }}</span>
                    </li>
                </ul>
            </AlertBox>

            <div class="flex flex-col">
                <div class="-mx-4 overflow-x-auto sm:-mx-6">
                    <div class="inline-block min-w-full align-middle px-4 sm:px-6">
                        <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead class="bg-gray-50">
                                <tr>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {{ $t('File in the folder') }}
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {{ $t('Customer no.') }}
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {{ $t('Year / month') }}
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {{ $t('Goes to') }}
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {{ $t('Note') }}
                                    </th>
                                </tr>
                                </thead>
                                <tbody class="bg-white divide-y divide-gray-200">
                                <template v-if="section.files.length > 0">
                                    <tr v-for="file in section.files" :key="file.filename">
                                        <td class="px-6 py-4 border-l-2" :class="accentFor(file)">
                                            <div class="text-sm text-gray-900 font-mono break-all">
                                                {{ file.filename }}
                                            </div>
                                            <div v-if="file.size !== null" class="text-xs text-gray-400 mt-1">
                                                {{ formatSize(file.size) }}
                                            </div>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm">
                                            <span v-if="file.internal_uid" class="text-gray-900">
                                                {{ file.internal_uid }}
                                            </span>
                                            <span v-else class="text-gray-300" aria-hidden="true">—</span>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm">
                                            <span v-if="file.year" class="text-gray-900">
                                                {{ file.year }}-{{ paddedMonth(file.month) }}
                                            </span>
                                            <span v-else class="text-gray-300" aria-hidden="true">—</span>
                                        </td>
                                        <td class="px-6 py-4">
                                            <div v-if="file.customer_name" class="text-sm text-gray-900">
                                                {{ file.customer_name }}
                                            </div>
                                            <div v-if="file.container_name" class="text-xs text-gray-400 mt-1">
                                                {{ file.container_name }}
                                            </div>
                                            <span v-if="!file.customer_name" class="text-sm text-gray-300"
                                                  aria-hidden="true">—</span>
                                        </td>
                                        <td class="px-6 py-4 text-sm">
                                            <div v-if="file.is_problem" class="font-medium text-danger-700">
                                                {{ problemNote(file) }}
                                            </div>
                                            <ul v-if="warnings(file).length > 0"
                                                class="text-warning-800"
                                                :class="{'mt-1': file.is_problem}">
                                                <li v-for="warning in warnings(file)" :key="warning">{{ warning }}</li>
                                            </ul>
                                        </td>
                                    </tr>
                                </template>
                                <template v-else>
                                    <tr>
                                        <td colspan="5" class="px-6 py-4 text-center text-sm text-gray-500">
                                            {{ $t('Nothing here yet. Upload the files to the path above, then re-check.') }}
                                        </td>
                                    </tr>
                                </template>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <p v-if="section.truncated" class="text-sm text-gray-500">
                {{ $t('Showing the first :shown of :total files — everything that needs attention is listed first.', {shown: section.shown_files, total: section.total_files}) }}
            </p>
        </div>
    </div>
</template>

<script>
import {defineComponent} from 'vue'
import AlertBox from '@/Components/AlertBox.vue';

export default defineComponent({
    components: {
        AlertBox,
    },

    props: {
        section: Object,
        title: String,
    },

    methods: {
        /**
         * Colour is spent only where reality deviates: a file that imports cleanly
         * carries no accent, so the rows that need a decision stand out down the
         * left edge of the table.
         */
        accentFor(file) {
            if (file.is_problem) {
                return 'border-danger-500';
            }
            if (this.warnings(file).length > 0) {
                return 'border-warning-500';
            }

            return 'border-transparent';
        },

        /**
         * Why this file would be skipped. Mirrors the ImportPreviewOutcome enum.
         */
        problemNote(file) {
            switch (file.outcome) {
                case 'invalid_file_name':
                    return this.$t('Name does not follow the pattern');
                case 'unknown_customer':
                    return this.$t('No customer has the number :number', {number: file.internal_uid});
                case 'missing_container':
                    return this.$t('This customer has no folder of this type');
                case 'unreadable_file':
                    return this.$t('Cannot be read from disk');
                default:
                    return file.outcome;
            }
        },

        /**
         * Conditions that do not stop the import but are worth a second look.
         */
        warnings(file) {
            const warnings = [];
            if (file.duplicate_name) {
                warnings.push(this.$t('Already stored — a second copy would be added'));
            }
            if (file.workspace_inactive) {
                warnings.push(this.$t('Customer is inactive'));
            }
            if (file.container_inactive) {
                warnings.push(this.$t('Target folder is inactive'));
            }

            return warnings;
        },

        paddedMonth(month) {
            return String(month).padStart(2, '0');
        },

        formatSize(bytes) {
            if (bytes < 1024) {
                return bytes + ' B';
            }
            if (bytes < 1024 * 1024) {
                return Math.round(bytes / 1024) + ' KB';
            }

            return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        },
    },
})
</script>
